import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import FamilyPage from './pages/FamilyPage';
import ReadPage from './pages/ReadPage';
import ReportReadingPage from './pages/ReportReadingPage';
import ProgressPage from './pages/ProgressPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import AdminLayout from './components/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminFamiliesPage from './pages/admin/AdminFamiliesPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminChallengesPage from './pages/admin/AdminChallengesPage';
import ChurchManagementPage from './pages/ChurchManagementPage';
import ChurchPage from './pages/ChurchPage';
import OnboardingPage from './pages/OnboardingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AppStorePage from './pages/AppStorePage';
import LeaderboardPage from './pages/LeaderboardPage';
import IndividualLeaderboardPage from './pages/IndividualLeaderboardPage';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';
import './reading-theme.css';
import './responsive.css';

// App content component that has access to auth context
const AppContent = () => {
  const { isAuthenticated, isLoading, onboarding, user, preferences } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--color-background)'
      }}>
        <LoadingSpinner size="large" message="Loading ReadTheWord..." />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        <Route 
          path="/auth" 
          element={
            (() => {
              if (!isAuthenticated) {
                return <AuthPage />;
              }
              
              console.log('🔍 Auth route redirect check:', {
                isAuthenticated,
                hasUser: !!user,
                hasOnboarding: !!onboarding,
                onboardingCompleted: onboarding?.completed,
                currentStep: onboarding?.currentStep
              });
              
              // If user needs onboarding, redirect to onboarding
              if (user && onboarding && !onboarding.completed) {
                console.log('🔍 Redirecting to onboarding from auth route');
                return <Navigate to="/onboarding" replace />;
              }
              
              console.log('🔍 Redirecting to home from auth route');
              return <Navigate to="/" replace />;
            })()
          } 
        />
        
        {/* Public community home and returning-reader quick log */}
        <Route path="/" element={
          (() => {
            console.log('🔍 Home route check:', {
              isAuthenticated,
              hasUser: !!user,
              hasOnboarding: !!onboarding,
              onboardingCompleted: onboarding?.completed,
              currentStep: onboarding?.currentStep,
              shouldRedirectToOnboarding: isAuthenticated && user && onboarding && !onboarding.completed
            });
            
            
            // If user is authenticated and needs onboarding, redirect to onboarding
            if (isAuthenticated && user && onboarding && !onboarding.completed) {
              console.log('🔍 Redirecting to onboarding from home route');
              return <Navigate to="/onboarding" replace />;
            }
            
            console.log('🔍 Showing home page');
            return (
              <Layout>
                <HomePage />
              </Layout>
            );
          })()
        } />

        {/* About page - accessible to both authenticated and unauthenticated users */}
        <Route path="/about" element={
          (() => {
            // If user is authenticated and needs onboarding, redirect to onboarding
            if (isAuthenticated && user && onboarding && !onboarding.completed) {
              return <Navigate to="/onboarding" replace />;
            }
            
            return (
              <Layout>
                <AboutPage />
              </Layout>
            );
          })()
        } />

        {/* Privacy Policy page - accessible to both authenticated and unauthenticated users */}
        <Route path="/privacy" element={
          (() => {
            // If user is authenticated and needs onboarding, redirect to onboarding
            if (isAuthenticated && user && onboarding && !onboarding.completed) {
              return <Navigate to="/onboarding" replace />;
            }
            
            return (
              <Layout>
                <PrivacyPolicyPage />
              </Layout>
            );
          })()
        } />

        {/* App Store page - accessible to both authenticated and unauthenticated users */}
        <Route path="/download" element={
          (() => {
            // If user is authenticated and needs onboarding, redirect to onboarding
            if (isAuthenticated && user && onboarding && !onboarding.completed) {
              return <Navigate to="/onboarding" replace />;
            }
            
            return (
              <Layout>
                <AppStorePage />
              </Layout>
            );
          })()
        } />
      
        {/* Protected routes - redirect to auth if not authenticated */}
        <Route path="/profile" element={
          isAuthenticated ? (
            <Navigate to="/settings#account" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/reading" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <ReadPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/family" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <FamilyPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/readers" element={
          isAuthenticated ? (
            <Navigate to="/family#readers" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/report" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <ReportReadingPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/manage-reports" element={
          isAuthenticated ? (
            <Navigate to="/progress#reports" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />
        <Route path="/my-points" element={
          isAuthenticated ? (
            <Navigate to={preferences?.competitiveMode ? '/progress#points' : '/progress'} replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/progress" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <ProgressPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/book-progress" element={
          isAuthenticated ? (
            <Navigate to="/progress#books" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/next-completion" element={
          isAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/reading-plans" element={
          isAuthenticated ? (
            <Navigate to="/reading#plans" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/leaderboard" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <LeaderboardPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/leaderboard/:category" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <IndividualLeaderboardPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/dashboard" element={
          isAuthenticated ? (
            <Navigate to="/progress" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/settings" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin" element={
          isAuthenticated ? (
            <Navigate to="/admin/dashboard" replace />
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        {/* New Admin Area Routes */}
        <Route path="/admin/dashboard" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/families" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminFamiliesPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/users" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminUsersPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/challenges" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminChallengesPage />
            </AdminLayout>
          </ProtectedRoute>
        } />

        {/* Redirect /admin/* routes to use AdminLayout for existing functionality */}
        <Route path="/admin/goals" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/reports" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/notifications" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/admin/settings" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <AdminLayout>
                <AdminPage />
              </AdminLayout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/church" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <ChurchPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        <Route path="/church-management" element={
          isAuthenticated ? (
            <ProtectedRoute>
              <Layout>
                <ChurchManagementPage />
              </Layout>
            </ProtectedRoute>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />

        {/* Onboarding route */}
        <Route path="/onboarding" element={
          (() => {
            console.log('🔍 Onboarding route check:', {
              isAuthenticated,
              hasUser: !!user,
              hasOnboarding: !!onboarding,
              onboardingCompleted: onboarding?.completed,
              currentStep: onboarding?.currentStep
            });
            
            if (!isAuthenticated) {
              console.log('🔍 Not authenticated, redirecting to auth');
              return <Navigate to="/auth" replace />;
            }
            
            if (!onboarding || onboarding.completed) {
              console.log('🔍 Onboarding completed or not available, redirecting to home');
              return <Navigate to="/" replace />;
            }
            
            console.log('🔍 Showing onboarding page');
            return <OnboardingPage />;
          })()
        } />

        {/* Redirect to onboarding if user needs to complete it, otherwise redirect to appropriate landing page */}
        <Route path="*" element={
          (() => {
            console.log('🔍 Wildcard route check:', {
              isAuthenticated,
              hasUser: !!user,
              hasOnboarding: !!onboarding,
              onboardingCompleted: onboarding?.completed,
              currentStep: onboarding?.currentStep,
              shouldRedirectToOnboarding: isAuthenticated && user && onboarding && !onboarding.completed
            });
            
            if (isAuthenticated && user && onboarding && !onboarding.completed) {
              console.log('🔍 Redirecting to onboarding from wildcard route');
              return <Navigate to="/onboarding" replace />;
            }
            
            // Redirect to about page for unauthenticated users, home for authenticated users
            const redirectPath = isAuthenticated ? "/" : "/about";
            console.log(`🔍 Redirecting to ${redirectPath} from wildcard route`);
            return <Navigate to={redirectPath} replace />;
          })()
        } />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;